import * as THREE from "three";
import type { DecorModelBounds } from "../../core/decor";

const MAX_RINGS = 256;
const MAX_WAKES = 128;
const SURFACE_OFFSET_M = 0.02;
const RING_MARGIN_M = 0.35;
const WAKE_LENGTH_FACTOR = 2.2;

const vertexShader = /* glsl */ `
  attribute float phase;
  varying vec2 vLocal;
  varying float vPhase;
  void main() {
    vLocal = position.xz;
    vPhase = phase;
    gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
  }
`;

const ringShader = /* glsl */ `
  uniform float time;
  uniform float opacity;
  varying vec2 vLocal;
  varying float vPhase;
  void main() {
    float r = length(vLocal);
    if (r > 1.0) discard;
    float angle = atan(vLocal.y, vLocal.x);
    float wobble = 0.025 * sin(angle * 5.0 + time * 1.7 + vPhase * 6.0);
    float alpha = 0.0;
    for (int k = 0; k < 3; k++) {
      float t = fract(time * 0.45 + vPhase + float(k) / 3.0);
      float radius = mix(0.55, 1.0, t) + wobble;
      float band = 1.0 - smoothstep(0.0, 0.045, abs(r - radius));
      alpha += band * (1.0 - t) * smoothstep(0.0, 0.15, t);
    }
    float foam = (1.0 - smoothstep(0.42, 0.6, r)) * smoothstep(0.3, 0.45, r) * (0.55 + 0.25 * sin(time * 3.0 + angle * 3.0 + vPhase * 6.0));
    gl_FragColor = vec4(1.0, 1.0, 1.0, clamp(alpha * 0.8 + foam * 0.5, 0.0, 1.0) * opacity);
  }
`;

const wakeShader = /* glsl */ `
  uniform float time;
  uniform float opacity;
  varying vec2 vLocal;
  varying float vPhase;
  void main() {
    float along = (vLocal.y + 1.0) * 0.5;
    float spread = 0.12 + along * 0.88;
    float side = abs(vLocal.x) / spread;
    if (side > 1.0) discard;
    float ripples = 0.5 + 0.5 * sin(along * 22.0 - side * 6.0 - time * 6.0 + vPhase * 6.0);
    float edge = (1.0 - smoothstep(0.0, 0.35, abs(side - 0.8))) * ripples;
    float trail = (1.0 - smoothstep(0.0, 0.45, side)) * 0.2 * ripples;
    float fade = pow(1.0 - along, 1.6) * smoothstep(0.0, 0.1, along);
    gl_FragColor = vec4(1.0, 1.0, 1.0, clamp(edge * 0.45 + trail, 0.0, 1.0) * fade * opacity);
  }
`;

function createMesh(shader: string, capacity: number, uniforms: Record<string, THREE.IUniform>): THREE.InstancedMesh {
  const geometry = new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2);
  geometry.setAttribute("phase", new THREE.InstancedBufferAttribute(new Float32Array(capacity), 1));
  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader,
    fragmentShader: shader,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.InstancedMesh(geometry, material, capacity);
  mesh.count = 0;
  mesh.frustumCulled = false;
  mesh.renderOrder = 2;
  return mesh;
}

function phaseOf(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return ((hash >>> 0) % 1000) / 1000;
}

/** Les remous autour de ce qui flotte et le sillage de ce qui nage, tous dessinés en deux appels. */
export class WaterRipples {
  readonly group = new THREE.Group();
  private readonly uniforms = { time: { value: 0 }, opacity: { value: 0.75 } };
  private readonly rings = createMesh(ringShader, MAX_RINGS, this.uniforms);
  private readonly wakes = createMesh(wakeShader, MAX_WAKES, this.uniforms);
  private readonly local = new THREE.Matrix4();
  private readonly matrix = new THREE.Matrix4();

  constructor() {
    this.group.add(this.rings, this.wakes);
  }

  set time(seconds: number) {
    this.uniforms.time.value = seconds;
  }

  begin(): void {
    this.rings.count = 0;
    this.wakes.count = 0;
  }

  /** `root` est l'objet posé : sa matrice porte sa position, son cap et son échelle. */
  add(id: string, root: THREE.Object3D, bounds: DecorModelBounds, moving: boolean): void {
    const centerX = (bounds.minX + bounds.maxX) / 2;
    const halfWidth = (bounds.maxX - bounds.minX) / 2;
    const halfLength = (bounds.maxY - bounds.minY) / 2;
    const phase = phaseOf(id);
    if (this.rings.count < MAX_RINGS) {
      this.local.makeScale(halfWidth * 1.5 + RING_MARGIN_M, 1, halfLength * 1.2 + RING_MARGIN_M);
      this.local.setPosition(centerX, SURFACE_OFFSET_M, -(bounds.minY + bounds.maxY) / 2);
      this.push(this.rings, root, phase);
    }
    if (moving && this.wakes.count < MAX_WAKES) {
      const length = halfLength * 2 * WAKE_LENGTH_FACTOR;
      this.local.makeScale(length * 0.4, 1, length / 2);
      this.local.setPosition(centerX, SURFACE_OFFSET_M, -bounds.minY - halfLength * 0.4 + length / 2);
      this.push(this.wakes, root, phase);
    }
  }

  private push(mesh: THREE.InstancedMesh, root: THREE.Object3D, phase: number): void {
    const index = mesh.count++;
    mesh.setMatrixAt(index, this.matrix.multiplyMatrices(root.matrixWorld, this.local));
    (mesh.geometry.getAttribute("phase") as THREE.InstancedBufferAttribute).setX(index, phase);
  }

  end(): void {
    for (const mesh of [this.rings, this.wakes]) {
      mesh.visible = mesh.count > 0;
      if (!mesh.count) continue;
      mesh.instanceMatrix.needsUpdate = true;
      mesh.geometry.getAttribute("phase").needsUpdate = true;
    }
  }

  dispose(): void {
    for (const mesh of [this.rings, this.wakes]) {
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
      mesh.dispose();
    }
  }
}
